import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { deleteAccount } from '../../../api/authApi';
import { useAuth } from '../../../context/AuthContext';
import styles from './EnterpriseContentPage.module.css';

export default function EnterpriseDeleteAccountPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDeleteRequest = async () => {
    if (!token) {
      setError('You need to log in again before deleting your account.');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const result = await deleteAccount(token);
      setMessage(result.detail || 'A confirmation email has been sent to your inbox.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request account deletion.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div style={{ maxWidth: 860, width: '100%', margin: '0 auto', display: 'grid', gap: 20 }}>
        <div>
          <h1 className={styles.title} style={{ marginBottom: 8 }}>Delete account</h1>
          <p style={{ margin: 0, color: 'var(--ds-text-muted)', lineHeight: 1.7 }}>
            Request permanent deletion of your Policyora account. This page will first send a confirmation email,
            and the account is deleted only after you approve the link in your inbox.
          </p>
        </div>

        <section
          style={{
            background: 'var(--ds-surface)',
            border: '1px solid var(--ds-border)',
            borderRadius: 18,
            padding: 24,
            boxShadow: '0 10px 30px rgba(16, 24, 40, 0.05)',
            display: 'grid',
            gap: 18,
          }}
        >
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ds-link)' }}>
              Before you continue
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--ds-text-secondary)', lineHeight: 1.8 }}>
              <li>A confirmation email will be sent to your registered email address.</li>
              <li>Your account is not deleted immediately from this page.</li>
              <li>Deletion becomes final only after you click the email confirmation link.</li>
              <li>Once confirmed, this action cannot be undone.</li>
            </ul>
          </div>

          <div
            style={{
              borderRadius: 14,
              border: '1px solid var(--ds-danger-bg)',
              background: 'var(--ds-danger-bg)',
              padding: '14px 16px',
              color: 'var(--ds-danger-text)',
              fontSize: 14,
              lineHeight: 1.6,
            }}
          >
            Use this only if you want to permanently close your enterprise workspace account.
          </div>

          {message && (
            <div style={{ borderRadius: 12, border: '1px solid var(--ds-success)', background: 'var(--ds-success-bg)', color: 'var(--ds-info-text)', padding: '12px 14px', fontSize: 14 }}>
              {message}
            </div>
          )}

          {error && (
            <div style={{ borderRadius: 12, border: '1px solid var(--ds-danger-bg)', background: 'var(--ds-danger-bg)', color: 'var(--ds-danger-text)', padding: '12px 14px', fontSize: 14 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <button
              type="button"
              onClick={handleDeleteRequest}
              disabled={loading}
              style={{
                border: 'none',
                borderRadius: 10,
                background: 'var(--ds-danger)',
                color: 'var(--ds-text-on-dark)',
                fontWeight: 700,
                padding: '11px 16px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Sending…' : 'Send delete confirmation email'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/enterprise/dashboard')}
              style={{
                borderRadius: 10,
                border: '1px solid var(--ds-border-strong)',
                background: 'var(--ds-surface)',
                color: 'var(--ds-text)',
                fontWeight: 700,
                padding: '11px 16px',
                cursor: 'pointer',
              }}
            >
              Back to dashboard
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
